import React, { useState } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { Card as CardBase } from './common';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { FollowableUser } from 'js/social/shared/types';
import BulkFollowListModal from 'js/social/shared/big_cards/BulkFollowListModal';
import { BulkFollowPreview } from 'js/social/shared/big_cards/BulkFollowPreview';
import { getFollowingSource } from 'js/social/shared/big_cards/bulk_follow_modal_helper';

const Card = styled(CardBase)`
  padding: 0;

  @include for-desktop-sm-up {
    padding: 20px 12px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid #cddaef;
  border-radius: 6px;
  background: #f1f2f9;
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.08);
`;

const MoreText = styled.a`
  color: $ds-primary-blue-active;
  font-weight: 500;
  cursor: pointer;
`;

export type BulkFollowCardData = {
  topic: {
    name: string;
    score: number;
  };
  users: FollowableUser[];
};

type BulkFollowCardProps = BulkFollowCardData & {
  altLook?: boolean;
  onClose: (followingsAdded?: boolean) => void;
};

export const BulkFollowCard: React.FC<BulkFollowCardProps> = ({
  altLook,
  topic,
  users,
  onClose,
}) => {
  const [showBulkFollowCard, setShowBulkFollowCard] = useState(true);
  const [showBulkFollowListModal, setShowBulkFollowListModal] = useState(false);
  const header = (
    <>
      Follow <span style={{ fontWeight: 500 }}>{topic.name}</span> academics
    </>
  );

  const description =
    users.length > 0 ? (
      <>
        Follow{' '}
        {users
          .slice(0, 3)
          .map((user) => user.displayName)
          .join(', ')}
        , plus{' '}
        <MoreText
          role="button"
          onClick={() => {
            setShowBulkFollowListModal(true);
          }}
        >
          {pluralize('more academic', Math.max(users.length - 3, 0), true)}
        </MoreText>
      </>
    ) : (
      <></>
    );

  const handleClose = (followingsAdded?: boolean) => {
    if (followingsAdded) {
      setShowBulkFollowCard(false);
    }
    onClose(followingsAdded);
  };

  return (
    <>
      {showBulkFollowCard && (
        <ImpressionWatcher source={'swp_bulk_follow_sidebar'}>
          <Card altLook={altLook}>
            <Container>
              <BulkFollowPreview
                topic={topic}
                header={header}
                description={description}
                users={users}
                followingSource={getFollowingSource('swp_sidebar')}
                variant="compact"
                onComplete={() => {
                  handleClose(true);
                }}
                onViewAll={() => setShowBulkFollowListModal(true)}
              />
            </Container>
          </Card>
        </ImpressionWatcher>
      )}
      {showBulkFollowListModal && (
        <BulkFollowListModal
          open={showBulkFollowListModal}
          initialFollowSuggestions={users.map((user) => {
            return {
              id: user.id,
              displayName: user.displayName,
              photoUrl: user.photo,
              universityName: user.university?.displayName,
              departmentName: user.department?.displayName,
              following: user.following,
            };
          })}
          cardType={'swp_sidebar'}
          onClose={(followingsAdded) => {
            setShowBulkFollowListModal(false);
            handleClose(followingsAdded);
          }}
          topic={topic}
        />
      )}
    </>
  );
};
